// Slot-mapping resolution utilities. The merge rule (per .design.md
// "Axis 6 — Slot mapping"):
//
//   For each slot id:
//     If the override has an entry for this slot → use the override's
//       array (which may be empty, meaning "render nothing").
//     Otherwise → use the layout's default array.
//
// Per-block merging is deliberately out of scope. A Designer that wants to
// keep three of the layout's five default blocks and add one of their own
// re-states the full slot list. Simpler semantic + makes the authoring UI
// (step 7) shape obvious — author edits a list, not a patch.

import type { BlockInstance, SlotMapping } from "./types";

/**
 * Coerce a raw override JSON object into a SlotMapping. The shape is the
 * Designer's stored override; we don't validate block kinds here — Slot.tsx's
 * dispatch already logs unknowns and skips. This function's job is just
 * type-tightening + drop entries that aren't arrays.
 */
export function coerceSlotMapping(raw: unknown): SlotMapping | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out: SlotMapping = {};
  for (const [slotId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(value)) continue;
    // Coerce each entry to BlockInstance — minimal shape check (must have
    // `block` and `key` strings). Anything else falls through to Slot.tsx
    // which logs + skips per-block.
    const entries: BlockInstance[] = [];
    for (const item of value) {
      if (!item || typeof item !== "object") continue;
      const o = item as Record<string, unknown>;
      if (typeof o.block !== "string" || typeof o.key !== "string") continue;
      entries.push(item as BlockInstance);
    }
    out[slotId] = entries;
  }
  return out;
}

/**
 * Per-slot merge: the override wins for any slot id it declares; everything
 * else falls back to the layout default. Returns a new map (never mutates).
 */
export function mergeSlotMappings(
  layoutDefault: SlotMapping,
  override: SlotMapping | null
): SlotMapping {
  if (!override) return layoutDefault;
  const out: SlotMapping = { ...layoutDefault };
  for (const [slotId, entries] of Object.entries(override)) {
    out[slotId] = entries;
  }
  return out;
}
